import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { projects } from "@/data/portfolio";

/**
 * Selected works.
 *
 * This used to run a hardcoded `layout` array cycled with i % layout.length,
 * described in the file as an "asymmetric grid". It was not composed, it was
 * arbitrary, and it produced a real defect: the array paired mismatched aspect
 * ratios inside the same row (16/11 landscape beside 4/5 portrait), so the
 * column spans summed to 12 and the widths worked, but the heights did not.
 * Measured on the running page, row one rendered frames of 465px and 584px and
 * the two captions beneath them sat 119px apart. Row two was out by 13px. Only
 * row three, where both aspects happened to match, aligned.
 *
 * Ragged caption baselines read as a rendering bug, not as art direction. Real
 * editorial asymmetry is aligned to something; this was aligned to nothing.
 *
 * So: one aspect ratio, one column width, six equal cards. Six projects of
 * equal standing do not need a hero slot, and uniformity here reads as
 * rigorous rather than plain. The craft has to show in the details instead,
 * which is where it belongs: a rule above every caption, identical caption
 * structure so the baselines cannot drift, and a hover that dims the siblings
 * rather than the whole grid.
 */
export const SelectedWorks = () => {
  return (
    <section
      id="work"
      className="relative bg-cream text-ink py-24 md:py-32"
      data-testid="selected-works-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-y-5 gap-x-6 md:gap-x-10 items-end mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <p className="chapter-num">Six projects · 2017 to today</p>
            <h2 className="mt-5 font-display text-display-l text-ink">
              The work itself.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 md:text-right font-sans font-light text-base md:text-lg text-ink2 leading-relaxed">
            Restorations, private residences, and the places I built and now
            run myself.
          </p>
        </motion.div>

        <div className="works-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-14 md:gap-y-16">
          {projects.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8% 0px" }}
              transition={{
                duration: 0.9,
                // Stagger across the row only, so row two does not wait on row one.
                delay: (i % 3) * 0.07,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="h-full"
            >
              <Link
                to={`/work/${p.slug}`}
                className="group/item flex h-full flex-col"
                data-testid={`project-card-${p.slug}`}
                data-cursor="view"
              >
                {/* One ratio for all six. This is the whole fix. */}
                <div className="img-frame aspect-[4/5]">
                  <img
                    src={p.thumb}
                    alt={p.title}
                    loading={i < 3 ? "eager" : "lazy"}
                  />
                </div>

                {/* Fixed structure, so no caption can drift out of line with
                    its neighbours regardless of how long the title runs. */}
                <div className="mt-5 flex flex-1 flex-col border-t border-ink pt-4">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="label text-ink2">
                      {String(i + 1).padStart(2, "0")} · {p.category}
                    </p>
                    <p className="label text-ink2">
                      {p.year}
                    </p>
                  </div>

                  <h3 className="mt-3 font-display text-display-s text-ink">
                    {p.title}
                  </h3>

                  <p className="mt-2 font-sans font-light text-[15px] text-ink2 leading-snug">
                    {p.subtitle}
                  </p>

                  <span className="mt-auto pt-4 inline-flex items-center gap-2 label text-ink2 group-hover/item:text-terracotta transition-colors">
                    View project
                    <ArrowUpRight
                      size={12}
                      className="transition-transform duration-300 group-hover/item:rotate-45"
                    />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
